import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { partnerApiService } from '../services/api';
import { WorkingHour, WorkingHourUpdateRequest } from '../types/api';
import { isRoundTheClock, ROUND_THE_CLOCK_HOURS } from '../utils/workingHours';

export interface WorkingHoursEditorHandle {
  saveAllDirty: () => Promise<{ saved: number; failed: number }>;
  hasDirty: () => boolean;
}

interface DayState {
    dayOfWeek: number;
    label: string;
    shortLabel: string;
    saved: WorkingHour | null;
    openTime: string;
    closeTime: string;
    isDayOff: boolean;
    nextDay: boolean;       // закрытие — следующий день (ночной режим)
    roundTheClock: boolean; // круглосуточно: 00:00 → 00:00 следующего дня, без перерыва
    hasBreak: boolean;
    breakStart: string;
    breakEnd: string;
    isLoading: boolean;
    error: string;
    isDirty: boolean;
}

/** Часы дня, которые правит партнёр, — без служебных полей строки. */
type DayHours = Pick<DayState,
    'openTime' | 'closeTime' | 'isDayOff' | 'nextDay' | 'roundTheClock' | 'hasBreak' | 'breakStart' | 'breakEnd'>;

/** Обычный рабочий день по умолчанию: 09:00–22:00, без ночной смены и перерыва. */
const DEFAULT_HOURS: DayHours = {
    openTime: '09:00', closeTime: '22:00', isDayOff: false, nextDay: false,
    roundTheClock: false, hasBreak: false, breakStart: '13:00', breakEnd: '14:00',
};

/** Круглосуточный день держит в состоянии те же времена, что уйдут на сервер. */
const ROUND_THE_CLOCK_DAY: DayHours = {
    ...DEFAULT_HOURS,
    openTime: ROUND_THE_CLOCK_HOURS.open_time,
    closeTime: ROUND_THE_CLOCK_HOURS.close_time,
    nextDay: ROUND_THE_CLOCK_HOURS.closes_next_day,
    roundTheClock: true,
};

const ALL_DAYS: { dayOfWeek: number; label: string; shortLabel: string }[] = [
    { dayOfWeek: 1, label: 'Понедельник', shortLabel: 'Пн' },
    { dayOfWeek: 2, label: 'Вторник', shortLabel: 'Вт' },
    { dayOfWeek: 3, label: 'Среда', shortLabel: 'Ср' },
    { dayOfWeek: 4, label: 'Четверг', shortLabel: 'Чт' },
    { dayOfWeek: 5, label: 'Пятница', shortLabel: 'Пт' },
    { dayOfWeek: 6, label: 'Суббота', shortLabel: 'Сб' },
    { dayOfWeek: 0, label: 'Воскресенье', shortLabel: 'Вс' },
];

function formatTime(time: string | null): string {
    if (!time) return '';
    return time.slice(0, 5);
}

// Ночная смена — по флагу closes_next_day ИЛИ по close < open: та же логика,
// что в бек-фильтре «открыто сейчас» (PartnerSearchService) и в мобилке.
// Сравнение времён — фолбэк для старых записей, заведённых без флага.
function isOvernight(wh: WorkingHour): boolean {
    return wh.closes_next_day
        || (!!wh.open_time && !!wh.close_time && wh.close_time < wh.open_time);
}

/** Пояс заведений — тот же, по которому бэк фильтрует «открыто сейчас»
 *  (config('app.business_timezone'), PartnerSearchService::applyOpenNow). */
const BUSINESS_TIMEZONE = 'Asia/Almaty';

/** Короткие имена дней Intl → нумерация бэка: 0 (вс) … 6 (сб). */
const WEEKDAY_INDEX: Record<string, number> = {
    Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6,
};

/**
 * «Сейчас» по поясу заведений: день недели (0=Вс…6=Сб) и минуты от полуночи.
 *
 * Бейдж «открыто сейчас» обязан совпадать с бек-фильтром, который живёт в
 * Asia/Almaty, а не с локальным поясом браузера партнёра — иначе партнёр в
 * поездке видит «закрыто», пока клиенты в Астане сидят в зале.
 */
function nowInBusinessTimezone(): { dayOfWeek: number; minutes: number } {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: BUSINESS_TIMEZONE,
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
    }).formatToParts(new Date());
    const part = (type: Intl.DateTimeFormatPartTypes): string =>
        parts.find((p) => p.type === type)?.value ?? '';
    return {
        dayOfWeek: WEEKDAY_INDEX[part('weekday')] ?? 0,
        minutes: Number(part('hour')) * 60 + Number(part('minute')),
    };
}

function isCurrentlyOpen(wh: WorkingHour | null): boolean {
    if (!wh || wh.is_day_off || !wh.open_time || !wh.close_time) return false;
    const { dayOfWeek: today, minutes: nowMin } = nowInBusinessTimezone();
    const [oh, om] = wh.open_time.split(':').map(Number);
    const [ch, cm] = wh.close_time.split(':').map(Number);
    const openMin = oh * 60 + om;
    const closeMin = ch * 60 + cm;

    let open = false;
    if (wh.day_of_week === today && isRoundTheClock(wh)) {
        // Круглосуточный день открыт в любую минуту своих суток — та же
        // ветка, что в бек-фильтре «открыто сейчас» (ADR-027). Без неё
        // «открытие = закрытие» без флага ночной смены не горел вовсе,
        // а 00:00–23:59 гас в последнюю минуту суток.
        open = true;
    } else if (wh.day_of_week === today) {
        // Сегодняшняя смена: ночная открыта с открытия и до полуночи.
        open = isOvernight(wh)
            ? nowMin >= openMin
            : nowMin >= openMin && nowMin < closeMin;
    } else if (wh.day_of_week === (today + 6) % 7) {
        // Хвост вчерашней ночной смены: в 01:00 заведение ещё открыто
        // по вчерашней записи — до её времени закрытия.
        open = isOvernight(wh) && nowMin < closeMin;
    }
    if (!open) return false;

    if (wh.break_start && wh.break_end) {
        const [bSh, bSm] = wh.break_start.split(':').map(Number);
        const [bEh, bEm] = wh.break_end.split(':').map(Number);
        const bStartMin = bSh * 60 + bSm;
        const bEndMin = bEh * 60 + bEm;
        if (nowMin >= bStartMin && nowMin < bEndMin) {
            return false;
        }
    }
    return true;
}

interface WorkingHoursEditorProps {
    initialHours?: WorkingHour[];
}

export const WorkingHoursEditor = forwardRef<WorkingHoursEditorHandle, WorkingHoursEditorProps>(({ initialHours }, ref) => {
    const [days, setDays] = useState<DayState[]>(() =>
        ALL_DAYS.map((d) => ({
            ...d,
            ...DEFAULT_HOURS,
            saved: null,
            isLoading: false,
            error: '',
            isDirty: false,
        }))
    );
    const [globalLoading, setGlobalLoading] = useState(false);
    const [globalError, setGlobalError] = useState('');

    const applyHours = useCallback((hours: WorkingHour[]) => {
        setDays((prev) => {
            // Профиль перезагружается и в фоне, и сразу после сохранения формы —
            // несохранённые правки партнёра при этом затирать нельзя.
            if (prev.some((d) => d.isDirty)) return prev;
            return prev.map((d) => {
                const found = hours.find((h) => h.day_of_week === d.dayOfWeek) || null;
                const openT = found?.open_time ? formatTime(found.open_time) : '09:00';
                const closeT = found?.close_time ? formatTime(found.close_time) : '22:00';
                const bStart = found?.break_start ? formatTime(found.break_start) : '13:00';
                const bEnd = found?.break_end ? formatTime(found.break_end) : '14:00';
                // Чекбокс восстанавливаем из closes_next_day ответа сервера;
                // close < open — фолбэк для старых записей, заведённых без флага.
                const isNextDay = found && !found.is_day_off && isOvernight(found);
                return {
                    ...d,
                    saved: found,
                    openTime: openT,
                    closeTime: closeT,
                    isDayOff: found ? found.is_day_off : false,
                    nextDay: !!isNextDay,
                    // По is_round_the_clock, а у старого сервера — по временам.
                    roundTheClock: !!found && isRoundTheClock(found),
                    hasBreak: !!(found?.break_start && found?.break_end),
                    breakStart: bStart,
                    breakEnd: bEnd,
                    isDirty: false,
                };
            });
        });
    }, []);

    useEffect(() => {
        if (initialHours && initialHours.length > 0) { applyHours(initialHours); return; }
        const load = async () => {
            setGlobalLoading(true); setGlobalError('');
            try { applyHours(await partnerApiService.getWorkingHours()); }
            catch { setGlobalError('Не удалось загрузить расписание'); }
            finally { setGlobalLoading(false); }
        };
        load();
    }, [initialHours, applyHours]);

    const updateDay = (dayOfWeek: number, patch: Partial<DayState>) =>
        setDays((prev) => prev.map((d) => d.dayOfWeek === dayOfWeek ? { ...d, ...patch, isDirty: true } : d));

    const handleSave = async (day: DayState): Promise<boolean> => {
        const isRoundTheClockDay = !day.isDayOff && day.roundTheClock;
        // У круглосуточного дня поля времени и перерыва скрыты — их не
        // проверяем: день уходит на сервер в каноничном виде.
        const hasHours = !day.isDayOff && !isRoundTheClockDay;
        if (hasHours && (!day.openTime || !day.closeTime)) {
            updateDay(day.dayOfWeek, { error: 'Укажите время открытия и закрытия' });
            return false;
        }
        if (hasHours && day.hasBreak && (!day.breakStart || !day.breakEnd)) {
            updateDay(day.dayOfWeek, { error: 'Укажите время начала и конца перерыва' });
            return false;
        }
        updateDay(day.dayOfWeek, { isLoading: true, error: '' });
        try {
            const payload: WorkingHourUpdateRequest = isRoundTheClockDay
                // Каноничные поля и флаг вместе (ADR-027): новый сервер сам
                // приводит день к 00:00 → 00:00⁺¹ без перерыва, старый
                // сохраняет времена как есть.
                ? { ...ROUND_THE_CLOCK_HOURS, is_round_the_clock: true }
                : {
                    open_time: day.isDayOff ? null : day.openTime,
                    close_time: day.isDayOff ? null : day.closeTime,
                    is_day_off: day.isDayOff,
                    // У выходного нет ночной смены: гейтим флаг, как остальные
                    // поля payload и как это делает мобилка.
                    closes_next_day: day.isDayOff ? false : day.nextDay,
                    break_start: (day.isDayOff || !day.hasBreak) ? null : day.breakStart,
                    break_end: (day.isDayOff || !day.hasBreak) ? null : day.breakEnd,
                    is_round_the_clock: false,
                };
            const result = day.saved
                ? await partnerApiService.updateWorkingHour(day.saved.id, payload)
                : await partnerApiService.createWorkingHour({ day_of_week: day.dayOfWeek, ...payload });
            const savedClose = formatTime(result.close_time);
            const savedOpen = formatTime(result.open_time);
            const savedBreakStart = formatTime(result.break_start);
            const savedBreakEnd = formatTime(result.break_end);
            setDays((prev) => prev.map((d) => d.dayOfWeek === day.dayOfWeek
                ? {
                    ...d, saved: result, openTime: savedOpen, closeTime: savedClose,
                    // Чекбокс — из ответа сервера, а не из сравнения времён:
                    // иначе галочка откатывалась сразу после сохранения.
                    isDayOff: result.is_day_off, nextDay: isOvernight(result),
                    roundTheClock: isRoundTheClock(result),
                    hasBreak: !!(result.break_start && result.break_end),
                    breakStart: savedBreakStart || '13:00',
                    breakEnd: savedBreakEnd || '14:00',
                    isLoading: false, isDirty: false, error: ''
                }
                : d
            ));
            return true;
        } catch (e: any) {
            const msg = e.response?.data?.message ||
                (e.response?.data?.errors ? Object.values(e.response.data.errors).flat().join(', ') : 'Ошибка сохранения');
            updateDay(day.dayOfWeek, { isLoading: false, error: msg });
            return false;
        }
    };

    useImperativeHandle(
        ref,
        () => ({
            hasDirty: () => days.some((d) => d.isDirty),
            saveAllDirty: async () => {
                const dirty = days.filter((d) => d.isDirty);
                if (dirty.length === 0) return { saved: 0, failed: 0 };
                let saved = 0;
                let failed = 0;
                for (const d of dirty) {
                    const ok = await handleSave(d);
                    if (ok) saved += 1;
                    else failed += 1;
                }
                return { saved, failed };
            },
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [days]
    );

    // Сброс возвращает день к значениям по умолчанию целиком, включая
    // ночную смену: до 01.10.2026 галочка «после полуночи» оставалась, и
    // сброшенный день сохранялся как 09:00 → 22:00 следующего дня.
    const RESET_DAY: Partial<DayState> = { ...DEFAULT_HOURS, isDirty: false, error: '' };

    // Галочка «Круглосуточно» у дня. Снятие возвращает обычные часы по
    // умолчанию, а не 00:00–00:00: иначе день так и остался бы сутками.
    const setRoundTheClock = (dayOfWeek: number, on: boolean) =>
        updateDay(dayOfWeek, on ? ROUND_THE_CLOCK_DAY : DEFAULT_HOURS);

    // Тумблер «круглосуточно, без выходных» горит, только когда так стоят
    // все семь дней. Он правит всю неделю разом и помечает дни изменёнными —
    // на сервер их отправит saveAllDirty по «Сохранить» формы.
    const isAllRoundTheClock = days.every((d) => !d.isDayOff && d.roundTheClock);
    const isSaving = days.some((d) => d.isLoading);
    const setAllRoundTheClock = (on: boolean) =>
        setDays((prev) => prev.map((d) => ({
            ...d, ...(on ? ROUND_THE_CLOCK_DAY : DEFAULT_HOURS), isDirty: true,
        })));

    const handleDelete = async (day: DayState) => {
        if (!day.saved) { updateDay(day.dayOfWeek, RESET_DAY); return; }
        updateDay(day.dayOfWeek, { isLoading: true, error: '' });
        try {
            await partnerApiService.deleteWorkingHour(day.saved.id);
            setDays((prev) => prev.map((d) => d.dayOfWeek === day.dayOfWeek
                ? { ...d, ...RESET_DAY, saved: null, isLoading: false }
                : d
            ));
        } catch { updateDay(day.dayOfWeek, { isLoading: false, error: 'Ошибка удаления' }); }
    };

    if (globalLoading) {
        return (
            <div style={{ display: 'grid', placeItems: 'center', padding: '28px 0' }}>
                <div className="m3-spinner" role="status" aria-label="Загрузка расписания" />
            </div>
        );
    }

    return (
        <div className="qp-stack" style={{ gap: 12 }}>
            {globalError && (
                <div className="qp-notice qp-notice--error" role="alert">
                    {globalError}
                </div>
            )}

            {/* Режим 24/7 одним движением, а не семь галочек и снятых
                выходных. Выключение возвращает все дни к 09:00–22:00. */}
            <button
                type="button"
                role="switch"
                aria-checked={isAllRoundTheClock}
                className="qp-toggle qp-toggle--bar"
                onClick={() => setAllRoundTheClock(!isAllRoundTheClock)}
                disabled={isSaving}
            >
                <span>Работаем круглосуточно, без выходных</span>
                <span className="qp-switch-track" aria-hidden>
                    <span />
                </span>
            </button>

            {/* Семь строк, разделённых волосяной линией: расписание читается
                как таблица, а не как семь отдельных карточек. */}
            <div className="qp-rows">
                {days.map((day) => {
                    const openNow = isCurrentlyOpen(day.saved);
                    return (
                        <div key={day.dayOfWeek} className={`qp-row${day.isDayOff ? ' qp-row--off' : ''}`}>
                            <span className="qp-row-title">
                                {day.label}
                                {openNow && (
                                    <span className="qp-status qp-status--ok" style={{ marginLeft: 8 }}>
                                        сейчас
                                    </span>
                                )}
                            </span>

                            <div className="qp-row-body">
                                {day.isDayOff ? (
                                    <span style={{ fontSize: 12.5, color: 'var(--qp2-faint)' }}>Закрыто весь день</span>
                                ) : day.roundTheClock ? (
                                    <span style={{ fontSize: 12.5, color: 'var(--qp2-text)' }}>Открыто 24 часа</span>
                                ) : (
                                    <>
                                        <input
                                            type="time"
                                            className="qp-time"
                                            value={day.openTime}
                                            onChange={(e) => updateDay(day.dayOfWeek, { openTime: e.target.value })}
                                            disabled={day.isLoading}
                                            aria-label={`${day.label}: открытие`}
                                        />
                                        <span className="qp-dash">—</span>
                                        <input
                                            type="time"
                                            className="qp-time"
                                            value={day.closeTime}
                                            onChange={(e) => updateDay(day.dayOfWeek, { closeTime: e.target.value })}
                                            disabled={day.isLoading}
                                            aria-label={`${day.label}: закрытие`}
                                        />
                                        {day.nextDay && <span className="qp-status">+1 день</span>}
                                        {day.hasBreak && (
                                            <span className="qp-status">
                                                перерыв {day.breakStart}–{day.breakEnd}
                                            </span>
                                        )}
                                    </>
                                )}
                            </div>

                            <div className="qp-row-tail">
                                <button
                                    type="button"
                                    role="switch"
                                    aria-checked={!day.isDayOff}
                                    className="qp-toggle"
                                    onClick={() => updateDay(day.dayOfWeek, { isDayOff: !day.isDayOff })}
                                    disabled={day.isLoading}
                                >
                                    <span>{day.isDayOff ? 'Выходной' : 'Открыто'}</span>
                                    <span className="qp-switch-track" aria-hidden>
                                        <span />
                                    </span>
                                </button>
                            </div>

                            {!day.isDayOff && (
                                <div className="qp-row-extra">
                                    {/* Первой в ряду: включённая прячет соседние
                                        настройки и сама при этом не уезжает
                                        из-под курсора. */}
                                    <label className="qp-checkline">
                                        <input
                                            type="checkbox"
                                            checked={day.roundTheClock}
                                            onChange={(e) => setRoundTheClock(day.dayOfWeek, e.target.checked)}
                                            disabled={day.isLoading}
                                        />
                                        Круглосуточно
                                    </label>
                                    {!day.roundTheClock && (
                                        <>
                                            <label className="qp-checkline">
                                                <input
                                                    type="checkbox"
                                                    checked={day.nextDay}
                                                    onChange={(e) => updateDay(day.dayOfWeek, { nextDay: e.target.checked })}
                                                    disabled={day.isLoading}
                                                />
                                                Закрытие после полуночи
                                            </label>
                                            <label className="qp-checkline">
                                                <input
                                                    type="checkbox"
                                                    checked={day.hasBreak}
                                                    onChange={(e) => updateDay(day.dayOfWeek, { hasBreak: e.target.checked })}
                                                    disabled={day.isLoading}
                                                />
                                                Перерыв
                                            </label>
                                            {day.hasBreak && (
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                                    <input
                                                        type="time"
                                                        className="qp-time"
                                                        value={day.breakStart}
                                                        onChange={(e) => updateDay(day.dayOfWeek, { breakStart: e.target.value })}
                                                        disabled={day.isLoading}
                                                        aria-label={`${day.label}: начало перерыва`}
                                                    />
                                                    <span className="qp-dash">—</span>
                                                    <input
                                                        type="time"
                                                        className="qp-time"
                                                        value={day.breakEnd}
                                                        onChange={(e) => updateDay(day.dayOfWeek, { breakEnd: e.target.value })}
                                                        disabled={day.isLoading}
                                                        aria-label={`${day.label}: конец перерыва`}
                                                    />
                                                </span>
                                            )}
                                        </>
                                    )}
                                    {day.saved && (
                                        <button
                                            type="button"
                                            className="qp-link"
                                            style={{ marginLeft: 'auto' }}
                                            onClick={() => handleDelete(day)}
                                            disabled={day.isLoading}
                                        >
                                            Сбросить день
                                        </button>
                                    )}
                                </div>
                            )}

                            {day.error && (
                                <span className="qp-field-error" style={{ flexBasis: '100%' }} role="alert">
                                    {day.error}
                                </span>
                            )}
                        </div>
                    );
                })}
            </div>

            <p className="qp-field-hint" style={{ margin: 0 }}>
                Расписание сохраняется вместе с профилем — кнопкой «Сохранить» вверху или внизу формы.
            </p>
        </div>
    );
});

WorkingHoursEditor.displayName = 'WorkingHoursEditor';

